import { useCallback, useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';

interface UseArticleScrollTrackingProps {
  articleId: string;
  dataLayer: any;
}

/**
 * Custom hook to track article scroll progress and fire GTM events at specific milestones
 * Milestones: 10%, 25%, 50%, 75%, 100%
 * The 10% milestone is delayed by 1.5s to allow GA to combine with virtualPageView event
 */
export const useArticleScrollTracking = ({ articleId, dataLayer }: UseArticleScrollTrackingProps) => {
  const articleRef = useRef<HTMLDivElement | null>(null);
  const progressMilestonesRef = useRef(new Set());
  const firstMilestoneFired = useRef(false);
  const firstMilestoneProcessing = useRef(false);
  // By request we are delaying the first 10% scroll event, so that GA can combine this with the virtualPageView event.
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = useCallback(() => {
    if (!articleRef.current) return;

    const articleElement = articleRef.current;
    const articleTop = articleElement.getBoundingClientRect().top;
    const articleHeight = articleElement.offsetHeight;
    const windowHeight = window.innerHeight;

    if (articleTop < windowHeight && articleTop + articleHeight > 0) {
      const scrollProgress = ((windowHeight - articleTop) / articleHeight) * 100;
      const scrollPercentage = Math.min(Math.max(scrollProgress, 0), 100);
      const milestones = [10, 25, 50, 75, 100];

      milestones.forEach((milestone) => {
        if (scrollPercentage >= milestone && !progressMilestonesRef.current.has(milestone)) {
          if (milestone === 10 && !firstMilestoneFired.current) {
            // Ensure 10% fires first.
            firstMilestoneFired.current = true;
            firstMilestoneProcessing.current = true;

            timeoutRef.current = setTimeout(() => {
              if (dataLayer) {
                dataLayer?.push({
                  event: 'scrollTracking',
                  articleId,
                  scrollDepth: milestone,
                  page: window.location.pathname,
                });
              }

              progressMilestonesRef.current.add(milestone);
              firstMilestoneProcessing.current = false;
              timeoutRef.current = null;
            }, 1500);
          } else if (milestone !== 10 && !firstMilestoneProcessing.current) {
            // Ensure other milestones fire only after 10% is logged.
            if (dataLayer) {
              dataLayer?.push({
                event: 'scrollTracking',
                articleId,
                scrollDepth: milestone,
                page: window.location.pathname,
              });
            }

            progressMilestonesRef.current.add(milestone);
          }
        }
      });
    }
  }, [articleId, dataLayer]);

  const throttledScrollHandler = useMemo(() => throttle(handleScroll, 200), [handleScroll]);

  useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler);

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      throttledScrollHandler.cancel();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [throttledScrollHandler]);

  return articleRef;
};
